import React from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { atTheBeginningOfDoc } from '@atlaskit/editor-common/selection';
import { type NextEditorPlugin } from '@atlaskit/editor-common/types';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';

export const arrowKeyPlugin: NextEditorPlugin<
	'arrowKeyPlugin',
	{
		pluginConfiguration: {
			onInputChange: () => void;
			onArrowUp: ({
				replaceDocument,
				view,
			}: {
				replaceDocument: (content: string | DocNode) => void;
				view: EditorView;
			}) => void;
			onArrowDown: ({
				replaceDocument,
				view,
			}: {
				replaceDocument: (content: string | DocNode) => void;
				view: EditorView;
			}) => void;
		};
	}
> = ({ config: { onArrowUp, onArrowDown, onInputChange }, api }) => {
	return {
		name: 'arrowKeyPlugin',
		pmPlugins() {
			return [
				{
					name: 'arrowKeyPlugin',
					plugin: () => {
						return new SafePlugin({
							props: {
								handleTextInput: () => {
									onInputChange();
								},
								handleKeyDown: (view: EditorView, event: KeyboardEvent) => {
									if (event.key === 'Backspace') {
										onInputChange();
									}

									if (!atTheBeginningOfDoc(view.state)) {
										return;
									}

									if (event.key === 'ArrowUp') {
										onArrowUp({
											replaceDocument: (value) => api?.core.actions.replaceDocument(value),
											view,
										});
									} else if (event.key === 'ArrowDown') {
										onArrowDown({
											replaceDocument: (value) => {
												api?.core.actions.replaceDocument(value);

												// Needed because the down arrow by default moves the cursor to the end of the input
												// preventDefault, stopPropagation, etc. doesn't work to prevent this in this callback
												// So we need to wait for the next tick to move the cursor to the start of the input
												new Promise((resolve) => {
													setTimeout(() => {
														view.dispatch(
															view.state.tr.setSelection(
																new TextSelection(
																	view.state.tr.doc.resolve(0),
																	view.state.tr.doc.resolve(0),
																),
															),
														);
														resolve(true);
													}, 0);
												});
											},
											view,
										});
									}
								},
							},
						});
					},
				},
			];
		},
	};
};

export type UserMessage = {
	id: string;
	content: string | DocNode;
};

export const useArrowkeyPlugin = ({ userMessages }: { userMessages: UserMessage[] }) => {
	const storeMessagesRef = React.useRef(userMessages);
	storeMessagesRef.current = userMessages;
	const currentMessageRef = React.useRef<UserMessage | undefined>(undefined);

	React.useEffect(() => {
		currentMessageRef.current = undefined;
	}, [userMessages.length]);

	const moveMessageUpOrDown = React.useCallback(
		({
			replaceDocument,
			position,
			view,
		}: {
			replaceDocument: (content: string | DocNode) => void;
			position: 'up' | 'down';
			view: EditorView;
		}) => {
			const increment = position === 'up' ? -1 : 1;
			const isEmpty = isEmptyDocument(view.state.doc);

			if (!storeMessagesRef.current.length) {
				return;
			}

			// If the input is not empty and the current message is not set(it's not the history message), do nothing
			if (!isEmpty && currentMessageRef.current === undefined) {
				return;
			}

			const currentMessageIndex = currentMessageRef.current
				? storeMessagesRef.current.findIndex(
						(message) => message.id === currentMessageRef.current?.id,
					)
				: storeMessagesRef.current.length;

			// If the currentindex is humanMessages.length, the next index will either be the last message (pressing up)
			// or out of bounds (pressing down and resetting the current message to undefined)
			const newMessageIndex = Math.max(0, currentMessageIndex + increment);

			const newMessage = storeMessagesRef.current[newMessageIndex];
			currentMessageRef.current = newMessage;
			replaceDocument(newMessage?.content ?? encodeAdf(''));
		},
		[],
	);

	const handleArrowUp = React.useCallback(
		({
			replaceDocument,
			view,
		}: {
			replaceDocument: (content: string | DocNode) => void;
			view: EditorView;
		}): void => {
			moveMessageUpOrDown({ replaceDocument, position: 'up', view });
		},
		[moveMessageUpOrDown],
	);

	const handleArrowDown = React.useCallback(
		({
			replaceDocument,
			view,
		}: {
			replaceDocument: (content: string | DocNode) => void;
			view: EditorView;
		}): void => {
			moveMessageUpOrDown({ replaceDocument, position: 'down', view });
		},
		[moveMessageUpOrDown],
	);

	const handleInputChange = React.useCallback((): void => {
		// Reset the current message to undefined when input gets updated
		currentMessageRef.current = undefined;
	}, []);

	return {
		handleArrowUp,
		handleArrowDown,
		handleInputChange,
	};
};
